import { Box, Divider, FormControl, Heading, HStack, Text, VStack, WarningOutlineIcon } from 'native-base';
import React, { useEffect, useMemo, useState } from 'react';
import { ValidationError } from 'yup';
import { RegistrationTypes } from '../../constants';
import {
  useDateSelectOptions,
  useGenderIdentificationSelectOptions,
  useRaceSelectOptions,
  useReferrerSelectOptions,
  useSchoolAccountSelectOptions,
} from '../../hooks';
import { useCountrySelectOptions } from '../../hooks/data/useCountryData';
import { useStateSelectOptions } from '../../hooks/data/useStateData';
import { useAppSelector } from '../../store';
import { RegistrationData } from '../../store/registration/types';
import { nativeAlert } from '../../utils/ui';
import { studentRegistrationFormSchema } from '../../validation/schemas/auth';
import { ValidationErrors } from '../../validation/types';
import { processValidationErrors } from '../../validation/utils';
import { Button } from '../ui';
import { CardContainer } from '../ui/cards/CardContainer';
import { EmailInput, Form, SelectInput, SelectInputOption, TextInput } from '../ui/forms';
import { FormProps } from '../ui/forms/types';

export interface StudentRegistrationFormProps extends FormProps {
  onSubmit: (data: RegistrationData) => void;
}

export const StudentRegistrationForm: React.FC<StudentRegistrationFormProps> = React.memo(
  ({ loading, disabled, onSubmit }) => {
    const authState = useAppSelector((state) => state.auth);
    const { currentUser } = authState;

    const { schoolAccountSelectOptions } = useSchoolAccountSelectOptions();
    const { stateSelectOptions } = useStateSelectOptions();
    const { countrySelectOptions } = useCountrySelectOptions();
    const { dateOptions, monthOptions, yearOptions } = useDateSelectOptions();
    const { raceSelectOptions } = useRaceSelectOptions();
    const { genderIdentificationSelectOptions } = useGenderIdentificationSelectOptions();
    const { referrerSelectOptions } = useReferrerSelectOptions();

    const [schoolAccountSfid, setSchoolAccountSfid] = useState('');
    const [firstName, setFirstName] = useState('');
    const [middleInitial, setMiddleInitial] = useState('');
    const [lastName, setLastName] = useState('');
    const [mobilePhone, setMobilePhone] = useState('');
    const [alternateEmail, setAlternateEmail] = useState('');
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [addressCity, setAddressCity] = useState('');
    const [addressState, setAddressState] = useState('');
    const [addressStateOther, setAddressStateOther] = useState('');
    const [addressCountry, setAddressCountry] = useState('');
    const [addressZip, setAddressZip] = useState('');
    const [dateOfBirthMonth, setDateOfBirthMonth] = useState('');
    const [dateOfBirthDay, setDateOfBirthDay] = useState('');
    const [dateOfBirthYear, setDateOfBirthYear] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [graduationYear, setGraduationYear] = useState('');
    const [genderIdentification, setGenderIdentification] = useState('');
    const [race, setRace] = useState('');
    const [referrer, setReferrer] = useState('');

    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

    const graduationYearOptions: SelectInputOption[] = useMemo(() => {
      const year = new Date().getFullYear();

      return Array.from({ length: 5 }, (_, i) => {
        const increment = i;
        return {
          label: String(year + increment),
          value: String(year + increment),
        };
      });
    }, []);

    useEffect(() => {
      if (dateOfBirthDay && dateOfBirthMonth && dateOfBirthYear) {
        const dateString = `${dateOfBirthYear}-${dateOfBirthMonth.padStart(2, '0')}-${dateOfBirthDay.padStart(2, '0')}`;
        setDateOfBirth(dateString);
      }
    }, [dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear]);

    const handleSubmit = () => {
      const data: RegistrationData = {
        type: RegistrationTypes.STUDENT,
        email: currentUser ? currentUser.email : '',
        schoolAccountSfid,
        firstName,
        middleInitial,
        lastName,
        mobilePhone,
        alternateEmail,
        addressLine1,
        addressLine2,
        addressCity,
        addressState,
        addressStateOther,
        addressZip,
        addressCountry,
        dateOfBirth,
        graduationYear,
        genderIdentification,
        referrer,
        race,
      };

      try {
        setValidationErrors({});
        const validated = studentRegistrationFormSchema.validateSync(data, { abortEarly: false });
        onSubmit(validated);
      } catch (err: any) {
        if (err instanceof ValidationError) {
          if (err instanceof ValidationError) {
            setValidationErrors(processValidationErrors(err));
            nativeAlert('Validation Error', 'Please check the form for errors.');
          } else {
            throw err;
          }
        }
      }
    };

    return (
      <Form>
        <Box p={4}>
          <Heading mb={4}>Student Registration</Heading>
          <CardContainer>
            <VStack space={4} mb={8}>
              <Text>
                Please review the release agreement and fill out the form below to complete your registration.
              </Text>
            </VStack>
            <Divider mb={8} />
            <VStack mb={4}>
              <Heading size="md">Basic Information</Heading>
              <SelectInput
                label="School Name"
                value={schoolAccountSfid}
                options={schoolAccountSelectOptions}
                onChange={setSchoolAccountSfid}
                disabled={disabled}
                loading={loading}
                error={validationErrors.schoolAccountSfid}
              />
              <TextInput
                value={firstName}
                onChange={setFirstName}
                label="First Name"
                disabled={disabled}
                loading={loading}
                error={validationErrors.firstName}
              />
              <TextInput
                value={middleInitial}
                onChange={setMiddleInitial}
                label="Middle Initial"
                inputProps={{ maxLength: 1 }}
                disabled={disabled}
                loading={loading}
                error={validationErrors.middleInitial}
              />
              <TextInput
                value={lastName}
                onChange={setLastName}
                label="Last Name"
                disabled={disabled}
                loading={loading}
                error={validationErrors.lastName}
              />
            </VStack>
            <VStack mb={4}>
              <Heading size="md">Contact Details</Heading>
              {/* TODO: Need a PhoneInput */}
              <TextInput
                value={mobilePhone}
                onChange={setMobilePhone}
                label="Student Mobile Phone"
                disabled={disabled}
                loading={loading}
                error={validationErrors.mobilePhone}
                helperText="UK Users: enter country code +44.  By providing your phone number, you agree to receive SMS notifications from Rock The Street, Wall Street. Message and data rates may apply."
              />
              <EmailInput
                value={alternateEmail}
                onChange={setAlternateEmail}
                label="Personal Email Address"
                disabled={disabled}
                loading={loading}
                error={validationErrors.alternateEmail}
              />
              <TextInput
                value={addressLine1}
                onChange={setAddressLine1}
                label="Address Line 1"
                disabled={disabled}
                loading={loading}
                error={validationErrors.addressLine1}
              />
              <TextInput
                value={addressLine2}
                onChange={setAddressLine2}
                label="Address Line 2"
                disabled={disabled}
                loading={loading}
                error={validationErrors.addressLine2}
              />
              <TextInput
                value={addressCity}
                onChange={setAddressCity}
                label="City"
                disabled={disabled}
                loading={loading}
                error={validationErrors.addressCity}
              />
              <HStack space={2}>
                <Box flex={2}>
                  <SelectInput
                    label="State"
                    value={addressState}
                    options={stateSelectOptions}
                    onChange={setAddressState}
                    disabled={disabled}
                    loading={loading}
                    error={validationErrors.addressState}
                  />
                </Box>
                <Box flex={1}>
                  <TextInput
                    value={addressZip}
                    onChange={setAddressZip}
                    label="Zip Code"
                    disabled={disabled}
                    loading={loading}
                    error={validationErrors.addressZip}
                  />
                </Box>
              </HStack>
              {addressState === 'other' ? (
                <TextInput
                  value={addressStateOther}
                  onChange={setAddressStateOther}
                  label="Other State/Province/Region"
                  disabled={disabled}
                  loading={loading}
                  error={validationErrors.addressStateOther}
                />
              ) : null}
              <SelectInput
                label="Country"
                value={addressCountry}
                options={countrySelectOptions}
                onChange={setAddressCountry}
                disabled={disabled}
                loading={loading}
                error={validationErrors.addressCountry}
              />
            </VStack>
            <VStack mb={4}>
              <Heading size="md">Demographics</Heading>
              <FormControl isInvalid={!!validationErrors.dateOfBirth}>
                <FormControl.Label>Date of birth</FormControl.Label>
                <HStack space={2}>
                  <Box flex={1.5}>
                    <SelectInput
                      label="Month"
                      placeholder="Month"
                      value={dateOfBirthMonth}
                      options={monthOptions}
                      onChange={setDateOfBirthMonth}
                      disabled={disabled}
                      loading={loading}
                      error={validationErrors.dateOfBirthMonth}
                    />
                  </Box>
                  <Box flex={1.25}>
                    <SelectInput
                      label="Date"
                      placeholder="Date"
                      value={dateOfBirthDay}
                      options={dateOptions}
                      onChange={setDateOfBirthDay}
                      disabled={disabled}
                      loading={loading}
                      error={validationErrors.dateOfBirthDay}
                    />
                  </Box>
                  <Box flex={1.25}>
                    <SelectInput
                      label="Year"
                      placeholder="Year"
                      value={dateOfBirthYear}
                      options={yearOptions}
                      onChange={setDateOfBirthYear}
                      disabled={disabled}
                      loading={loading}
                      error={validationErrors.dateOfBirthYear}
                    />
                  </Box>
                </HStack>
                <Box mt={-2} mb={2}>
                  <FormControl.ErrorMessage leftIcon={<WarningOutlineIcon size="xs" />}>
                    {validationErrors.dateOfBirth}
                  </FormControl.ErrorMessage>
                </Box>
              </FormControl>
              <SelectInput
                label="Graduation Year"
                value={graduationYear}
                options={graduationYearOptions}
                onChange={setGraduationYear}
                disabled={disabled}
                loading={loading}
                error={validationErrors.graduationYear}
              />
              <SelectInput
                label="Race"
                value={race}
                options={raceSelectOptions}
                onChange={setRace}
                disabled={disabled}
                loading={loading}
                error={validationErrors.race}
              />
              <SelectInput
                label="Gender Identification"
                value={genderIdentification}
                options={genderIdentificationSelectOptions}
                onChange={setGenderIdentification}
                disabled={disabled}
                loading={loading}
                error={validationErrors.genderIdentification}
              />
              <SelectInput
                label="How did you hear about RTSWS?"
                value={referrer}
                options={referrerSelectOptions}
                onChange={setReferrer}
                disabled={disabled}
                loading={loading}
                error={validationErrors.referrer}
              />
            </VStack>

            <Button
              onPress={() => handleSubmit()}
              disabled={disabled}
              loading={loading}
              text="Save and Continue"
              color="primary"
            />
          </CardContainer>
        </Box>
      </Form>
    );
  },
);
